import { useCallback, useEffect, useRef } from 'react';

import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import TextFormatRoundedIcon from '@mui/icons-material/TextFormatRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import WifiTetheringRoundedIcon from '@mui/icons-material/WifiTetheringRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { testTelegramConnection } from '../../api/telegramSettingsApi';
import {
  useMaxSettings,
  useTelegramSettings,
  useTenantSettings,
  useUpdateMaxSettings,
  useUpdateTelegramSettings,
  useUpdateTenantSettings,
  useUploadTenantLogo,
} from '../../hooks';
import {
  emptyBusinessInformation,
  emptyContactInformation,
  toSettingsUpdate,
  useDomainPermissions,
  useFeedback,
  useSettingsForm,
} from '../../lib';
import type {
  BusinessInformation,
  ContactInformation,
  MaxSettings,
  SalesMode,
  TelegramParseMode,
  TenantSettings,
  UpdateMaxSettingsRequest,
  UpdateTenantSettingsRequest,
} from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  FieldRow,
  NumberField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TestConnectionButton,
  TextInputField,
} from '../components';
import {
  CURRENCY_OPTIONS,
  LANGUAGE_OPTIONS,
  SALES_MODE_HELP,
  SALES_MODE_OPTIONS,
  TIMEZONE_OPTIONS,
} from '../options';

const PARSE_MODE_OPTIONS = [
  { value: 'HTML', label: 'HTML' },
  { value: 'MarkdownV2', label: 'MarkdownV2' },
];

const TELEGRAM_SALES_CHANNEL_CODE = 'TELEGRAM_MINI_APP_SHOP';

function buildTenantSettingsUpdate(
  draft: TenantSettings,
  currentLogoUrl: string | null | undefined,
): UpdateTenantSettingsRequest {
  const update = toSettingsUpdate(draft);
  if (update.businessInformation) {
    const requestedLogoUrl = update.businessInformation.logoUrl?.trim();
    update.businessInformation = {
      ...update.businessInformation,
      logoUrl: requestedLogoUrl ? requestedLogoUrl : (currentLogoUrl ?? null),
    };
  }
  return update;
}

function buildMaxSettingsUpdate(draft: MaxSettings): UpdateMaxSettingsRequest {
  // botSecretConfigured is a read-only server indicator — strip it before saving.
  // updatedAt / updatedBy are server-managed and excluded by the UpdateMaxSettingsRequest type;
  // destructuring them here makes the narrowing explicit so TypeScript stays happy.
  const { botSecretConfigured, updatedAt, updatedBy, ...rest } = draft;
  return rest;
}

// ── Shared secondary-save action bar ──────────────────────────────────────────

/**
 * Lightweight save/reset bar rendered below each independent settings section.
 * The primary SettingsScaffold save handles only TelegramSettings (section 3).
 */
function SecondaryFormActions({
  dirty,
  saving,
  dirtyLabel,
  cleanLabel,
  saveLabel,
  onSave,
  onReset,
}: {
  dirty: boolean;
  saving: boolean;
  dirtyLabel: string;
  cleanLabel: string;
  saveLabel: string;
  onSave: () => void;
  onReset: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <Typography variant="body2" color={dirty ? 'text.primary' : 'text.secondary'}>
        {dirty ? dirtyLabel : cleanLabel}
      </Typography>
      <Button onClick={onReset} disabled={!dirty || saving}>
        Reset
      </Button>
      <Button
        variant="outlined"
        onClick={onSave}
        disabled={!dirty || saving}
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
      >
        {saving ? 'Saving…' : saveLabel}
      </Button>
    </Box>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function CompanyLogoField({
  value,
  disabled,
  uploading,
  onPickFile,
}: {
  value: string | null | undefined;
  disabled: boolean;
  uploading: boolean;
  onPickFile: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    onPickFile(file);
  };

  return (
    <FieldRow
      label="Logo asset"
      description="Upload an image file. The file is stored as a public STORE_LOGO asset and the returned public URL is saved in tenant settings."
      alignTop
    >
      <Stack spacing={1.5}>
        {value ? (
          <Box
            component="img"
            src={value}
            alt="Company logo preview"
            sx={{
              maxWidth: 240,
              maxHeight: 96,
              width: 'fit-content',
              objectFit: 'contain',
              borderRadius: 2,
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: 'background.paper',
              p: 1,
            }}
          />
        ) : (
          <Alert severity="info" variant="outlined">
            No company logo uploaded yet.
          </Alert>
        )}

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Button
            type="button"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadRoundedIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={disabled || uploading}
          >
            {value ? 'Replace company logo' : 'Upload company logo'}
          </Button>
          {value ? (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {value}
            </Typography>
          ) : null}
        </Stack>

        {/* aria-hidden + tabIndex ensure the input is fully absent from the
            accessibility tree; display:none is more reliable than `hidden`
            across browser/AT combinations. */}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          aria-hidden="true"
          tabIndex={-1}
          style={{ display: 'none' }}
          onChange={(event) => {
            handleFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
      </Stack>
    </FieldRow>
  );
}

function BusinessProfileSection({
  value,
  contactValue,
  onChange,
  onContactChange,
  disabled,
}: {
  value: BusinessInformation;
  contactValue: ContactInformation;
  onChange: (value: BusinessInformation) => void;
  onContactChange: (value: ContactInformation) => void;
  disabled: boolean;
}) {
  return (
    <SettingsCard
      title="Business profile"
      description="Public company information shown across the Telegram storefront and legal pages."
      icon={<LanguageRoundedIcon />}
    >
      <TextInputField
        label="Store name"
        value={value.storeName ?? ''}
        onChange={(next) => onChange({ ...value, storeName: next })}
        disabled={disabled}
      />
      <SelectField
        label="Time zone"
        value={value.timezone ?? ''}
        options={TIMEZONE_OPTIONS}
        onChange={(next) => onChange({ ...value, timezone: next })}
        disabled={disabled}
      />
      <SelectField
        label="Currency"
        value={value.currency ?? ''}
        options={CURRENCY_OPTIONS}
        onChange={(next) => onChange({ ...value, currency: next })}
        disabled={disabled}
      />
      <SelectField
        label="Locale"
        description="Default storefront locale used for public content and formatting."
        value={value.locale ?? ''}
        options={LANGUAGE_OPTIONS}
        onChange={(next) => onChange({ ...value, locale: next })}
        disabled={disabled}
      />
      <TextInputField
        label="Telegram"
        description="Public contact handle shown in the Telegram storefront."
        value={contactValue.telegram ?? ''}
        onChange={(next) => onContactChange({ ...contactValue, telegram: next })}
        disabled={disabled}
      />
      <TextInputField
        label="Website"
        description="Public website URL shown in the Telegram storefront."
        value={contactValue.website ?? ''}
        onChange={(next) => onContactChange({ ...contactValue, website: next })}
        disabled={disabled}
        type="url"
      />
    </SettingsCard>
  );
}

// ── Page component ─────────────────────────────────────────────────────────────

/**
 * Unified Telegram settings page — single surface for the full Telegram
 * customer-facing platform.
 *
 * **Section 1 — Storefront (TenantSettings)**
 * Public profile, logo, sales mode, and capability flags propagated via Kafka to
 * `web-tenant-settings-service` → `web-catalog-bff` → `telegram-catalog`
 * (`StorefrontGuard`).
 *
 * **Section 2 — Bot & Channel (MaxSettings)**
 * Channel feature flags, bot identity, and write-only credentials propagated via
 * Kafka to `web-telegram-service` (bot routing, FAQ, support, AI agent) and
 * `web-auth-service` (Mini App `initData` HMAC validation via bot token sync).
 *
 * **Section 3 — Messaging service (TelegramSettings)**
 * Service-behavioural settings consumed directly by `web-telegram-service`:
 * message formatting, delivery, rate limits, and inbound fallback.
 *
 * Each section saves independently (optimistic-lock `version` per resource).
 * Sections 1 and 2 show propagation feedback to signal eventual-consistency
 * Kafka delivery. Section 3 uses the primary `SettingsScaffold` save action.
 */
export function TelegramSettingsPage() {
  // ── Permissions ──────────────────────────────────────────────────────────────
  const { canView, canManage } = useDomainPermissions('telegram');
  const tenantPermissions = useDomainPermissions('tenant');
  const maxPermissions = useDomainPermissions('max');

  // ── Queries & mutations ──────────────────────────────────────────────────────
  const query = useTelegramSettings();
  const update = useUpdateTelegramSettings();
  const tenantQuery = useTenantSettings();
  const tenantUpdate = useUpdateTenantSettings();
  const maxQuery = useMaxSettings();
  const maxUpdate = useUpdateMaxSettings();
  const uploadLogo = useUploadTenantLogo();

  // ── Draft forms (independent per resource) ───────────────────────────────────
  const form = useSettingsForm(query.data);
  const tenantForm = useSettingsForm(tenantQuery.data);
  const maxForm = useSettingsForm(maxQuery.data);

  const { feedback, propagation, success, error: notifyError, clear } = useFeedback();

  // Keep a live ref to tenantForm.draft so the async logo-upload onSuccess callback
  // always reads the latest draft state, not the stale closure captured at call time.
  const tenantFormDraftRef = useRef(tenantForm.draft);
  useEffect(() => {
    tenantFormDraftRef.current = tenantForm.draft;
  }, [tenantForm.draft]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  // Section 3 — TelegramSettings (primary SettingsScaffold save)
  const handleSave = useCallback(() => {
    if (!form.draft) return;
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Telegram messaging settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) void query.refetch();
      },
    });
  }, [form.draft, update.mutate, success, notifyError, query.refetch]);

  // Section 1 — TenantSettings → Kafka → web-catalog-bff · telegram-catalog
  const handleSaveStorefront = useCallback(() => {
    if (!tenantForm.draft) return;
    tenantUpdate.mutate(
      buildTenantSettingsUpdate(tenantForm.draft, tenantQuery.data?.businessInformation?.logoUrl),
      {
        onSuccess: () => propagation('Telegram storefront settings saved.'),
        onError: (error) => {
          notifyError(error.message);
          if (error.status === 409) void tenantQuery.refetch();
        },
      },
    );
  }, [tenantForm.draft, tenantUpdate.mutate, tenantQuery.data, tenantQuery.refetch, propagation, notifyError]);

  // Section 2 — MaxSettings → Kafka → web-telegram-service · web-auth-service
  const handleSaveMax = useCallback(() => {
    if (!maxForm.draft) return;
    maxUpdate.mutate(buildMaxSettingsUpdate(maxForm.draft), {
      onSuccess: () => propagation('Bot and channel settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) void maxQuery.refetch();
      },
    });
  }, [maxForm.draft, maxUpdate.mutate, maxQuery.refetch, propagation, notifyError]);

  // Logo upload → CDN URL written into tenantForm draft
  const handleLogoUpload = useCallback(
    (file: File) => {
      uploadLogo.mutate(
        { file, ownerId: TELEGRAM_SALES_CHANNEL_CODE },
        {
          onSuccess: (asset) => {
            tenantForm.setField('businessInformation', {
              ...(tenantFormDraftRef.current?.businessInformation ?? emptyBusinessInformation()),
              logoUrl: asset.publicUrl,
            });
            success('Company logo uploaded. Save Telegram storefront settings to publish it.');
          },
          onError: (error) => notifyError(error.message),
        },
      );
    },
    [uploadLogo.mutate, tenantForm.setField, success, notifyError],
  );

  return (
    <SettingsScaffold
      title="Telegram settings"
      subtitle="Storefront, bot channel, and messaging service settings for the full Telegram customer experience."
      icon={<TelegramIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending || tenantUpdate.isPending || maxUpdate.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) return null;
        const telegramDisabled = !canManage;
        const tenantDisabled = telegramDisabled || !tenantPermissions.canManage;
        const maxDisabled = !maxPermissions.canManage;
        const credentialDisabled = maxDisabled || !maxPermissions.canManageCredentials;

        return (
          <Stack spacing={2.5}>
            {/* ─────────────────────────────────────────────────────────────────
                Section 1 — Storefront
                TenantSettings → web-tenant-settings-service
                             → Kafka → web-catalog-bff · telegram-catalog
            ──────────────────────────────────────────────────────────────────── */}

            <Divider>
              <Typography variant="overline" color="text.secondary">
                Storefront
              </Typography>
            </Divider>

            {tenantQuery.isError ? (
              <Alert
                severity="error"
                variant="outlined"
                action={
                  <Button size="small" onClick={() => void tenantQuery.refetch()}>
                    Retry
                  </Button>
                }
              >
                {"Couldn’t load storefront settings. Messaging settings below are not affected."}
              </Alert>
            ) : null}

            {tenantQuery.isSuccess && tenantForm.draft ? (
              <>
                <BusinessProfileSection
                  value={tenantForm.draft.businessInformation ?? emptyBusinessInformation()}
                  contactValue={tenantForm.draft.contactInformation ?? emptyContactInformation()}
                  onChange={(value) => tenantForm.setField('businessInformation', value)}
                  onContactChange={(value) => tenantForm.setField('contactInformation', value)}
                  disabled={tenantDisabled}
                />

                <SettingsCard
                  title="Company logo"
                  description="Upload a public logo asset; the returned CDN URL is stored in tenant settings and served via web-catalog-bff."
                  icon={<CloudUploadRoundedIcon />}
                >
                  <CompanyLogoField
                    value={tenantForm.draft.businessInformation?.logoUrl}
                    disabled={tenantDisabled}
                    uploading={uploadLogo.isPending}
                    onPickFile={handleLogoUpload}
                  />
                </SettingsCard>
              </>
            ) : null}

            <SettingsCard
              title="Storefront status"
              description="Master storefront switch and sales mode — evaluated by web-catalog-bff and StorefrontGuard in telegram-catalog."
              icon={<StorefrontRoundedIcon />}
            >
              {tenantQuery.isLoading ? (
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {'Loading storefront settings…'}
                  </Typography>
                </Box>
              ) : tenantQuery.isError ? (
                <Box sx={{ py: 2 }}>
                  <Alert severity="warning" variant="outlined">
                    Storefront status is temporarily unavailable.
                  </Alert>
                </Box>
              ) : tenantForm.draft ? (
                <>
                  <SwitchField
                    label="Storefront enabled"
                    description="Master switch for the online store opened from Telegram."
                    checked={tenantForm.draft.storefrontEnabled ?? false}
                    onChange={(value) => tenantForm.setField('storefrontEnabled', value)}
                    disabled={tenantDisabled}
                  />
                  <SelectField
                    label="Sales mode"
                    description={SALES_MODE_HELP[tenantForm.draft.salesMode ?? 'PRE_OPENING']}
                    value={tenantForm.draft.salesMode ?? 'PRE_OPENING'}
                    options={SALES_MODE_OPTIONS}
                    onChange={(value) => tenantForm.setField('salesMode', value as SalesMode)}
                    disabled={tenantDisabled}
                  />
                  <Box sx={{ pt: 1 }}>
                    <Alert severity="info" variant="outlined">
                      Sales mode gates the Telegram storefront. In <strong>PRE_OPENING</strong> and{' '}
                      <strong>PAUSED</strong>, purchasing is restricted. In{' '}
                      <strong>MAINTENANCE</strong>, the storefront is unavailable.
                    </Alert>
                  </Box>
                </>
              ) : null}
            </SettingsCard>

            <SettingsCard
              title="Storefront capabilities"
              description="Customer purchasing funnel flags served by web-catalog-bff and enforced by StorefrontGuard in telegram-catalog."
              icon={<ShoppingCartCheckoutRoundedIcon />}
            >
              {tenantQuery.isLoading ? (
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {'Loading storefront capabilities…'}
                  </Typography>
                </Box>
              ) : tenantQuery.isError ? (
                <Box sx={{ py: 2 }}>
                  <Alert severity="warning" variant="outlined">
                    Storefront capabilities are temporarily unavailable.
                  </Alert>
                </Box>
              ) : tenantForm.draft ? (
                <>
                  <SwitchField
                    label="Catalog browsing"
                    description="Customers can browse products and categories."
                    checked={tenantForm.draft.catalogEnabled ?? false}
                    onChange={(value) => tenantForm.setField('catalogEnabled', value)}
                    disabled={tenantDisabled}
                  />
                  <SwitchField
                    label="Shopping basket"
                    description="Customers can add products to a cart."
                    checked={tenantForm.draft.basketEnabled ?? false}
                    onChange={(value) => tenantForm.setField('basketEnabled', value)}
                    disabled={tenantDisabled}
                  />
                  <SwitchField
                    label="Checkout"
                    description="Customers can start the checkout flow."
                    checked={tenantForm.draft.checkoutEnabled ?? false}
                    onChange={(value) => tenantForm.setField('checkoutEnabled', value)}
                    disabled={tenantDisabled}
                  />
                  <SwitchField
                    label="Orders"
                    description="Customers can place and track orders."
                    checked={tenantForm.draft.ordersEnabled ?? false}
                    onChange={(value) => tenantForm.setField('ordersEnabled', value)}
                    disabled={tenantDisabled}
                  />
                  <SwitchField
                    label="Online payment"
                    description="Online payment is available at checkout."
                    checked={tenantForm.draft.paymentEnabled ?? false}
                    onChange={(value) => tenantForm.setField('paymentEnabled', value)}
                    disabled={tenantDisabled}
                  />
                  <Box sx={{ pt: 1 }}>
                    <Alert severity="info" variant="outlined">
                      Capabilities are also gated by the sales mode — ordering and payment stay off
                      until the storefront is <strong>ACTIVE</strong>, regardless of these toggles.
                    </Alert>
                  </Box>
                </>
              ) : null}
            </SettingsCard>

            {tenantQuery.isSuccess && tenantPermissions.canManage ? (
              <SecondaryFormActions
                dirty={tenantForm.dirty}
                saving={tenantUpdate.isPending}
                dirtyLabel="Telegram storefront changes are not saved"
                cleanLabel="Telegram storefront changes saved"
                saveLabel="Save Telegram storefront"
                onSave={handleSaveStorefront}
                onReset={tenantForm.reset}
              />
            ) : null}

            {/* ─────────────────────────────────────────────────────────────────
                Section 2 — Bot & Channel
                MaxSettings → Kafka → web-telegram-service (bot routing, features,
                                                            FAQ, support, AI agent)
                           → Kafka → web-auth-service (botToken → initData HMAC)
            ──────────────────────────────────────────────────────────────────── */}

            <Divider>
              <Typography variant="overline" color="text.secondary">
                {'Bot & Channel'}
              </Typography>
            </Divider>

            {maxQuery.isLoading ? (
              <Box sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {'Loading bot and channel settings…'}
                </Typography>
              </Box>
            ) : maxQuery.isError ? (
              <Alert
                severity="error"
                variant="outlined"
                action={
                  <Button size="small" onClick={() => void maxQuery.refetch()}>
                    Retry
                  </Button>
                }
              >
                {"Couldn’t load bot and channel settings. Messaging settings below are not affected."}
              </Alert>
            ) : maxForm.draft ? (
              <>
                {!maxPermissions.canManageCredentials && maxPermissions.canView ? (
                  <Alert severity="info">
                    You can edit channel behaviour, but bot token and webhook secret require the
                    credentials permission.
                  </Alert>
                ) : null}

                <SettingsCard
                  title="Telegram channel"
                  description="Feature flags consumed by web-telegram-service. Changes propagate asynchronously via Kafka."
                  icon={<ChatRoundedIcon />}
                >
                  <SwitchField
                    label="Channel enabled"
                    description="Turn the Telegram bot channel on or off for this tenant."
                    checked={maxForm.draft.maxEnabled}
                    onChange={(value) => maxForm.setField('maxEnabled', value)}
                    disabled={maxDisabled}
                  />
                  <SwitchField
                    label="Mini App enabled"
                    description="Allow customers to open the telegram-catalog Mini App from the bot."
                    checked={maxForm.draft.miniAppEnabled}
                    onChange={(value) => maxForm.setField('miniAppEnabled', value)}
                    disabled={maxDisabled}
                  />
                  <TextInputField
                    label="Mini App URL"
                    description="Public URL of the telegram-catalog Mini App opened from the bot's storefront button."
                    value={maxForm.draft.miniAppUrl ?? ''}
                    onChange={(value) => maxForm.setField('miniAppUrl', value)}
                    disabled={maxDisabled}
                    type="url"
                  />
                  <SwitchField
                    label="Support enabled"
                    description="Expose support and contact entry points in the bot."
                    checked={maxForm.draft.supportEnabled}
                    onChange={(value) => maxForm.setField('supportEnabled', value)}
                    disabled={maxDisabled}
                  />
                  <TextInputField
                    label="Support contact"
                    description="Phone, username, or contact shown to customers when escalating to a human agent."
                    value={maxForm.draft.supportContact ?? ''}
                    onChange={(value) => maxForm.setField('supportContact', value)}
                    disabled={maxDisabled}
                  />
                  <SwitchField
                    label="Phone collection"
                    description="Ask customers for a phone number during the bot registration flow."
                    checked={maxForm.draft.phoneCollectionEnabled}
                    onChange={(value) => maxForm.setField('phoneCollectionEnabled', value)}
                    disabled={maxDisabled}
                  />
                  <SwitchField
                    label="FAQ enabled"
                    description="Show FAQ and help content inside the bot channel."
                    checked={maxForm.draft.faqEnabled}
                    onChange={(value) => maxForm.setField('faqEnabled', value)}
                    disabled={maxDisabled}
                  />
                  <SwitchField
                    label="Support AI agent"
                    description="Route support requests through the AI assistant before escalating to a human."
                    checked={maxForm.draft.supportAiAgentEnabled}
                    onChange={(value) => maxForm.setField('supportAiAgentEnabled', value)}
                    disabled={maxDisabled}
                  />
                </SettingsCard>

                <SettingsCard
                  title="Bot identity"
                  description="Bot registration details used by web-telegram-service for registry lookups and routing."
                  icon={<SmartToyRoundedIcon />}
                >
                  <TextInputField
                    label="Bot code"
                    description="Stable internal identifier used for routing and Vault secret storage."
                    value={maxForm.draft.botCode ?? ''}
                    onChange={(value) => maxForm.setField('botCode', value)}
                    disabled={maxDisabled}
                  />
                  <TextInputField
                    label="Bot username"
                    description="Public Telegram bot username without the @ prefix."
                    value={maxForm.draft.botUsername ?? ''}
                    onChange={(value) => maxForm.setField('botUsername', value)}
                    disabled={maxDisabled}
                  />
                  <TextInputField
                    label="Bot name"
                    description="Display name shown to customers in the Telegram chat client."
                    value={maxForm.draft.botName ?? ''}
                    onChange={(value) => maxForm.setField('botName', value)}
                    disabled={maxDisabled}
                  />
                  <SwitchField
                    label="Bot enabled"
                    description="Enable the bot endpoint and inbound routing in web-telegram-service."
                    checked={maxForm.draft.botEnabled}
                    onChange={(value) => maxForm.setField('botEnabled', value)}
                    disabled={maxDisabled}
                  />
                  <FieldRow
                    label="Credentials status"
                    description="Secret values are write-only and never returned to the browser."
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      color={maxForm.draft.botSecretConfigured ? 'success' : 'default'}
                      label={maxForm.draft.botSecretConfigured ? 'Configured' : 'Not configured'}
                    />
                  </FieldRow>
                </SettingsCard>

                <SettingsCard
                  title="Bot credentials"
                  description="Write-only bot secrets. The bot token is synced to web-auth-service via Kafka for Mini App initData HMAC validation."
                  icon={<SecurityRoundedIcon />}
                >
                  <FieldRow
                    label="Bot token"
                    description="Leave blank to keep the current token. Synced to web-auth-service for Mini App initData validation."
                    alignTop
                  >
                    <TextField
                      size="small"
                      fullWidth
                      type="password"
                      value={maxForm.draft.botToken ?? ''}
                      disabled={credentialDisabled}
                      onChange={(event) => maxForm.setField('botToken', event.target.value)}
                      autoComplete="new-password"
                    />
                  </FieldRow>
                  <FieldRow
                    label="Webhook secret"
                    description="Leave blank to keep the current secret. Used by web-telegram-service to verify inbound updates from Telegram."
                    alignTop
                  >
                    <TextField
                      size="small"
                      fullWidth
                      type="password"
                      value={maxForm.draft.webhookSecret ?? ''}
                      disabled={credentialDisabled}
                      onChange={(event) => maxForm.setField('webhookSecret', event.target.value)}
                      autoComplete="new-password"
                    />
                  </FieldRow>
                </SettingsCard>

                {maxPermissions.canManage ? (
                  <SecondaryFormActions
                    dirty={maxForm.dirty}
                    saving={maxUpdate.isPending}
                    dirtyLabel="Bot and channel settings are not saved"
                    cleanLabel="Bot and channel settings saved"
                    saveLabel={'Save bot & channel'}
                    onSave={handleSaveMax}
                    onReset={maxForm.reset}
                  />
                ) : null}
              </>
            ) : null}

            {/* ─────────────────────────────────────────────────────────────────
                Section 3 — Messaging service
                TelegramSettings → web-telegram-service directly (sync update,
                no Kafka fan-out — primary SettingsScaffold save action)
            ──────────────────────────────────────────────────────────────────── */}

            <Divider>
              <Typography variant="overline" color="text.secondary">
                Messaging service
              </Typography>
            </Divider>

            <SettingsCard title="Messaging" icon={<TuneRoundedIcon />}>
              <SwitchField
                label="Notification rules"
                description="Enable Telegram notification rules for this store."
                checked={draft.notificationRulesEnabled}
                onChange={(value) => form.setField('notificationRulesEnabled', value)}
                disabled={telegramDisabled}
              />
              <SwitchField
                label="Marketing opt-in"
                description="Allow customers to opt in to marketing messages via Telegram."
                checked={draft.marketingOptInEnabled}
                onChange={(value) => form.setField('marketingOptInEnabled', value)}
                disabled={telegramDisabled}
              />
              <SwitchField
                label="Order status messages"
                description="Send order status updates to customers through Telegram."
                checked={draft.orderStatusMessagesEnabled}
                onChange={(value) => form.setField('orderStatusMessagesEnabled', value)}
                disabled={telegramDisabled}
              />
            </SettingsCard>

            <SettingsCard title="Message formatting" icon={<TextFormatRoundedIcon />}>
              <SelectField
                label="Parse mode"
                description="How the bot renders message text — HTML tags or MarkdownV2 syntax."
                value={draft.defaultParseMode}
                options={PARSE_MODE_OPTIONS}
                onChange={(value) => form.setField('defaultParseMode', value as TelegramParseMode)}
                disabled={telegramDisabled}
              />
              <SwitchField
                label="Disable web page preview"
                description="Suppress link previews in outgoing messages."
                checked={draft.disableWebPagePreview}
                onChange={(value) => form.setField('disableWebPagePreview', value)}
                disabled={telegramDisabled}
              />
            </SettingsCard>

            <SettingsCard title="Delivery & limits" icon={<SpeedRoundedIcon />}>
              <NumberField
                label="Max retries"
                description="Maximum delivery attempts before a message is marked as failed."
                value={draft.deliveryMaxRetries}
                onChange={(value) => form.setField('deliveryMaxRetries', value ?? 0)}
                disabled={telegramDisabled}
                min={0}
                max={10}
              />
              <NumberField
                label="Retry backoff"
                description="Delay between consecutive delivery retries."
                value={draft.deliveryRetryBackoffMs}
                onChange={(value) => form.setField('deliveryRetryBackoffMs', value ?? 0)}
                disabled={telegramDisabled}
                min={0}
                suffix="ms"
              />
              <NumberField
                label="Send timeout"
                description="Maximum time to wait for a successful send before failing the attempt."
                value={draft.sendTimeoutMs}
                onChange={(value) => form.setField('sendTimeoutMs', value ?? 0)}
                disabled={telegramDisabled}
                min={0}
                suffix="ms"
              />
              <NumberField
                label="Rate limit"
                description="Maximum messages the bot may send per chat per minute."
                value={draft.rateLimitPerChatPerMin}
                onChange={(value) => form.setField('rateLimitPerChatPerMin', value ?? 1)}
                disabled={telegramDisabled}
                min={1}
                suffix="/ min"
              />
              <SwitchField
                label="Inbound fallback"
                description="Route unrecognised inbound messages to a fallback handler."
                checked={draft.inboundFallbackEnabled}
                onChange={(value) => form.setField('inboundFallbackEnabled', value)}
                disabled={telegramDisabled}
              />
            </SettingsCard>

            <SettingsCard title="Connection" icon={<WifiTetheringRoundedIcon />}>
              <FieldRow
                label="Test connection"
                description="Verify the bot is reachable and the Telegram API is responding."
                alignTop
              >
                <TestConnectionButton run={testTelegramConnection} disabled={!canManage} />
              </FieldRow>
            </SettingsCard>

            <SettingsCard
              title="Bot messages"
              description="Canned replies (/start, /help, /support, …) and the FAQ are edited on a dedicated screen."
              icon={<ForumRoundedIcon />}
              action={
                <Button
                  size="small"
                  variant="outlined"
                  component={RouterLink}
                  to="/settings/telegram/messages"
                >
                  Open editor
                </Button>
              }
              divided={false}
            >
              <Typography variant="body2" color="text.secondary" sx={{ pt: 1.5 }}>
                Customise your bot's reply texts per command and manage the FAQ used for free-text
                questions. Messages you don't customise fall back to the platform defaults.
              </Typography>
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
